import { useEffect, useState } from "react";

import { Theme } from "@mui/material";
import Box from "@mui/material/Box";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";

import { fToNow } from "@onair/minimal-ui/utils/format-time";

import { NotificationResponse } from "@onair/repositories";
import { actionMaskAsRead } from "@onair/store/notifications";

import { NotificationAvatar } from "#src/components/notifications/notification-avatar";

const formatNotification = (data: NotificationResponse) => {
	const isEmptyDescription = !data.description || data.description === "";

	return (
		<Stack>
			<Typography
				variant="subtitle2"
				fontSize={14}
				sx={{
					mb: isEmptyDescription ? 0 : 0.5,
				}}
			>
				{data.title}
			</Typography>
			{!isEmptyDescription && (
				<Box
					dangerouslySetInnerHTML={{ __html: data.description! }}
					sx={{
						mb: 0.5,
						fontSize: 12,
						"& p": { typography: "body2", m: 0, fontSize: 12 },
						"& a": { color: "inherit", textDecoration: "none" },
						"& strong": { typography: "subtitle2" },
					}}
				/>
			)}
		</Stack>
	);
};

export enum NotificationVariant {
	DEFAULT = "default",
	CONTAINED = "contained",
}

interface Props {
	notification: NotificationResponse;
	variant?: NotificationVariant;
}

export const NotificationItem = ({
	notification,
	variant = NotificationVariant.DEFAULT,
}: Props) => {
	const [isRead, setIsRead] = useState(notification.read || false);

	useEffect(() => {
		setIsRead(notification.read || false);
	}, [notification.read]);

	const renderText = (
		<ListItemText
			disableTypography
			primary={formatNotification(notification)}
			sx={{
				mr: 1.5,
			}}
			secondary={
				<Stack
					direction="row"
					alignItems="center"
					sx={{ typography: "caption", color: "text.disabled" }}
					divider={
						<Box
							sx={{
								width: 2,
								height: 2,
								bgcolor: "currentColor",
								mx: 0.5,
								borderRadius: "50%",
							}}
						/>
					}
				>
					{fToNow(notification.createdAt)}
				</Stack>
			}
		/>
	);

	const renderUnReadBadge = !isRead && (
		<Box
			sx={{
				top: 24,
				width: 8,
				height: 8,
				right: 20,
				borderRadius: "50%",
				bgcolor: "primary.light",
				position: "absolute",
			}}
		/>
	);

	const itemSx = (() => {
		switch (variant) {
			case NotificationVariant.CONTAINED:
				return {
					borderBottom: (theme: Theme) => `dashed 1px ${theme.vars.palette.divider}`,
				};
			default:
				return {
					borderRadius: 2,
					border: "1px solid rgb(145 158 171 / 15%)",
					boxShadow: "0px 12px 24px -4px rgba(145, 158, 171, 0.12)",
				};
		}
	})();

	const handleClickNotification = async () => {
		if (!isRead) {
			await actionMaskAsRead(notification.id);
			setIsRead(true);
		}

		if (notification.webUrl) {
			return (window.location.href = notification.webUrl);
		}
	};

	return (
		<ListItemButton
			disableRipple
			sx={{
				p: 2.5,
				alignItems: "flex-start",
				position: "relative",
				...itemSx,
			}}
			component="a"
			onClick={handleClickNotification}
		>
			{renderUnReadBadge}

			<NotificationAvatar notification={notification} />

			<Stack sx={{ flexGrow: 1 }}>{renderText}</Stack>
		</ListItemButton>
	);
};
